import { Button } from '@zorro/design-system/v2/Button';
import { Select } from '@zorro/design-system/v2/Select';
import { Center, Stack, Text } from '@zorro/web/design-system';
import { useBatchCallEndpoint, useIdempotencyKey } from '@zorro/web/ui-utils';
import { useState } from 'react';

import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';

function calculateInitialActiveStatus(recordsForActions: RecordForActions[]) {
  if (recordsForActions.length > 1) {
    const onBoarding = { active: 0, inactive: 0 };
    recordsForActions.forEach((record) => {
      if (record.isOnboardingActive) {
        onBoarding.active = onBoarding.active + 1;
      } else {
        onBoarding.inactive = onBoarding.inactive + 1;
      }
    });
    return onBoarding.active > onBoarding.inactive;
  }
  return recordsForActions[0].isOnboardingActive || false;
}

export type ChangeShoppingStatusFormProps = {
  recordsForActions: RecordForActions[];
  onSuccess: () => void;
};

export const ChangeShoppingStatusForm = ({
  recordsForActions,
  onSuccess,
}: ChangeShoppingStatusFormProps) => {
  const idempotencyKey = useIdempotencyKey();
  const [isActive, setIsActive] = useState<boolean>(() =>
    calculateInitialActiveStatus(recordsForActions)
  );
  const { executeBatchCall, errors } = useBatchCallEndpoint({
    methodName: 'onboardingPeriodsControllerUpdateByAdmin',
    singularItemName: 'onboarding period',
    action: 'updated',
    batchSize: 5,
  });

  async function changeOnboardingStatus() {
    const batchItems = recordsForActions.map(
      ({ onboardingPeriodId, fullName }) => ({
        key: fullName,
        params: [
          onboardingPeriodId as string, // modal is only available for employees with an onboarding period
          idempotencyKey.getFor(onboardingPeriodId as string),
          { isActive },
        ] as const,
      })
    );

    await executeBatchCall(batchItems, { onSuccess });
  }

  async function handleSubmit() {
    await changeOnboardingStatus();
  }

  return (
    <Stack>
      <Stack>
        <Text>Set shopping status as:</Text>
        <Select
          defaultValue={isActive ? 'Active' : 'Inactive'}
          data={[
            { value: 'Active', label: 'Active' },
            { value: 'Inactive', label: 'Inactive' },
          ]}
          onChange={(value) => {
            setIsActive(value === 'Active');
          }}
        />
      </Stack>

      <Center>
        <Button onClick={handleSubmit} size="lg">
          Save
        </Button>
      </Center>

      <ErrorsTable
        tableTitle="Employee name"
        errors={errors}
        isBulk={recordsForActions?.length > 1}
      />
    </Stack>
  );
};
